/*
 * LogisticsFoodPage — restaurant/cafe/bar browser with ordering.
 *
 * Reuses the venue browse backend (category: 'restaurant') that already
 * supports restaurants, cafes, fast food, bars, and pubs via Overpass API.
 * Menu items are deterministically generated per venue. Ordering follows the
 * same two-phase wallet flow as accommodation bookings.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ForkKnife, MapPinLine, MagnifyingGlass,
  ListBullets, MapTrifold, ArrowRight,
} from '@phosphor-icons/react';

import LogisticsLayout from './LogisticsLayout';
import { useLogisticsExercise } from '../../context/LogisticsExerciseContext';
import VenueMap, { VenueDetailCard } from './VenueMap';
import VenueCard from '../../components/logistics/VenueCard';
import FoodFilters from '../../components/logistics/FoodFilters';
import RestaurantDetailPanel from '../../components/logistics/RestaurantDetailPanel';
import FoodOrderDrawer from '../../components/logistics/FoodOrderDrawer';
import logisticsApi from '../../lib/logisticsApi';
import { useWebSocket } from '../../context/WebSocketContext';

const PRESET_LOCATIONS = [
  { name: 'London',     center: [-0.13, 51.51],     zoom: 13, image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=600&h=400&fit=crop', country: 'United Kingdom' },
  { name: 'Manchester', center: [-2.244, 53.483],    zoom: 13, image: 'https://images.unsplash.com/photo-1515586838455-8f8f940d6853?w=600&h=400&fit=crop', country: 'United Kingdom' },
  { name: 'Edinburgh',  center: [-3.19, 55.95],      zoom: 13, image: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&h=400&fit=crop', country: 'Scotland' },
  { name: 'New York',   center: [-73.985, 40.748],   zoom: 13, image: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=600&h=400&fit=crop', country: 'United States' },
  { name: 'Tokyo',      center: [139.755, 35.68],    zoom: 13, image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&h=400&fit=crop', country: 'Japan' },
  { name: 'Sydney',     center: [151.209, -33.865],  zoom: 13, image: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=600&h=400&fit=crop', country: 'Australia' },
];

const FoodContent = () => {
  const { getSocket } = useWebSocket();
  const { exercises, selected: ctxExercise, selectedId: exerciseId } = useLogisticsExercise();
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const [center, setCenter] = useState(PRESET_LOCATIONS[0].center);
  const [zoom, setZoom] = useState(PRESET_LOCATIONS[0].zoom);

  const [wallets, setWallets] = useState([]);

  // Panels
  const [detailPanelVenue, setDetailPanelVenue] = useState(null);
  const [orderDrawerVenue, setOrderDrawerVenue] = useState(null);
  const [orderDrawerItems, setOrderDrawerItems] = useState([]);

  // View toggle
  const [viewMode, setViewMode] = useState('cards');

  // Browse state
  const [browseVenues, setBrowseVenues] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browseOffset, setBrowseOffset] = useState(0);
  const [presetCities, setPresetCities] = useState([]);

  // Filters
  const [selectedCities, setSelectedCities] = useState(['London']);
  const [searchText, setSearchText] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState(null);
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [browseError, setBrowseError] = useState(null);

  // Location search
  const [locationQuery, setLocationQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeLocation, setActiveLocation] = useState(null);
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const suggestionsRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const skipAutocompleteRef = React.useRef(false);

  const exercise = ctxExercise;
  const currencySymbol = exercise?.currency_symbol || 'Ÿ';

  useEffect(() => {
    if (!exerciseId) return;
    logisticsApi.presetCities(exerciseId)
      .then((r) => setPresetCities(r.cities || []))
      .catch(() => setPresetCities(PRESET_LOCATIONS.map((l) => l.name)));
  }, [exerciseId]);

  // Autocomplete
  useEffect(() => {
    if (skipAutocompleteRef.current) { skipAutocompleteRef.current = false; return; }
    if (!locationQuery || locationQuery.length < 2 || !exerciseId) { setSuggestions([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setGeocodeLoading(true);
      try {
        const res = await logisticsApi.geocodeSearch(exerciseId, locationQuery);
        setSuggestions(res.suggestions || []);
        setShowSuggestions(true);
      } catch { setSuggestions([]); }
      finally { setGeocodeLoading(false); }
    }, 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [locationQuery, exerciseId]);

  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadWallets = useCallback(async () => {
    if (!exerciseId) { setWallets([]); return; }
    try {
      const w = await logisticsApi.myWallets();
      const all = [...(w.personal || []), ...(w.team || [])];
      setWallets(all.filter((x) => x.logistics_exercise_id === exerciseId && !x.frozen));
    } catch { setWallets([]); }
  }, [exerciseId]);

  useEffect(() => { loadWallets(); }, [loadWallets]);

  // Browse venues — category: 'restaurant'
  const doBrowse = useCallback(async (append = false, locationOverride = null) => {
    if (!exerciseId) return;
    const loc = locationOverride || activeLocation;
    if (!loc && selectedCities.length === 0) return;
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      const offset = append ? browseOffset + 24 : 0;
      const filters = {
        category: 'restaurant',
        subcategories: subcategoryFilter ? [subcategoryFilter] : [],
        min_price: priceMin ? priceMin * 100 : null,
        max_price: priceMax ? priceMax * 100 : null,
        search: searchText || null,
        limit: 24,
        offset,
      };
      if (loc) { filters.location = loc; filters.cities = []; }
      else { filters.cities = selectedCities; }
      const res = await logisticsApi.browseVenues(exerciseId, filters);
      if (res.error) {
        setBrowseError(res.error);
        if (!append) setBrowseVenues([]);
        setBrowseTotal(0);
      } else {
        if (append) setBrowseVenues((prev) => [...prev, ...(res.venues || [])]);
        else setBrowseVenues(res.venues || []);
        setBrowseTotal(res.total || 0);
        if (res.geocoded) {
          setActiveLocation(res.geocoded.name);
          setSelectedCities(res.cities || [res.geocoded.name]);
        }
      }
      setBrowseOffset(offset);
    } catch (e) {
      setBrowseError(e.message);
      if (!append) setBrowseVenues([]);
    } finally { setBrowseLoading(false); }
  }, [exerciseId, selectedCities, activeLocation, subcategoryFilter, priceMin, priceMax, searchText, browseOffset]);

  useEffect(() => {
    if (exerciseId && !initialLoadDone) {
      setInitialLoadDone(true);
      doBrowse(false);
    }
  }, [exerciseId, initialLoadDone, doBrowse]);

  const upsertWallet = useCallback((wallet) => {
    if (!wallet?.wallet_id) return;
    setWallets((prev) => prev.map((w) => (w.wallet_id === wallet.wallet_id ? { ...w, ...wallet } : w)));
  }, []);

  useEffect(() => {
    const socket = getSocket?.();
    if (!socket) return;
    const onEvent = (data) => {
      if (data?.order?.logistics_exercise_id && data.order.logistics_exercise_id !== exerciseId) return;
      upsertWallet(data?.wallet);
    };
    const events = ['ls_food_order_created', 'ls_food_order_confirmed', 'ls_food_order_cancelled',
                    'ls_booking_created', 'ls_booking_confirmed', 'ls_booking_cancelled'];
    events.forEach((e) => socket.on(e, onEvent));
    return () => events.forEach((e) => socket.off(e, onEvent));
  }, [getSocket, exerciseId, upsertWallet]);

  const [pendingBrowse, setPendingBrowse] = useState(false);

  const handleCityClick = (cityName) => {
    setSelectedCities([cityName]);
    setActiveLocation(null);
    setLocationQuery('');
    setViewMode('cards');
    setBrowseOffset(0);
    setPendingBrowse(true);
  };

  const handleLocationSelect = (suggestion) => {
    skipAutocompleteRef.current = true;
    setLocationQuery(suggestion.name);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveLocation(suggestion.name);
    setSelectedCities([]);
    setViewMode('cards');
    setBrowseOffset(0);
    doBrowse(false, suggestion.name);
  };

  const handleLocationSearch = () => {
    if (!locationQuery.trim()) return;
    setShowSuggestions(false);
    setActiveLocation(locationQuery.trim());
    setSelectedCities([]);
    setBrowseOffset(0);
    doBrowse(false, locationQuery.trim());
  };

  useEffect(() => {
    if (pendingBrowse && exerciseId) {
      setPendingBrowse(false);
      doBrowse(false);
    }
  }, [pendingBrowse, selectedCities, exerciseId, doBrowse]);

  const bookDisabledReason = useMemo(() => {
    if (wallets.length === 0) return 'No spendable wallet for this exercise.';
    if (exercise && !['active', 'draft'].includes(exercise.status)) {
      return `Exercise '${exercise.name}' is ${exercise.status} — all orders are closed.`;
    }
    return null;
  }, [wallets, exercise]);

  const handleViewDetails = (venue) => setDetailPanelVenue(venue);
  const handleQuickBook = (venue) => setDetailPanelVenue(venue);
  const handleOrder = (venue, cartItems) => {
    setDetailPanelVenue(null);
    setOrderDrawerVenue(venue);
    setOrderDrawerItems(cartItems);
  };

  const jumpTo = (loc) => {
    setCenter(loc.center);
    setZoom(loc.zoom);
    setSelectedVenue(null);
    setMapKey((k) => k + 1);
  };

  return (
    <div className="ls-page" data-testid="ls-food-page">
      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-xl mb-6"
        style={{ height: 'clamp(180px, 30vh, 260px)' }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1600&h=600&fit=crop)' }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.2) 100%)' }} />
        <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <ForkKnife size={18} weight="fill" style={{ color: 'var(--ls-primary)' }} />
            <span className="ls-overline tracking-[0.15em]" style={{ color: 'var(--ls-primary)' }}>FOOD & CATERING</span>
          </div>
          <h1 className="ls-heading text-2xl sm:text-3xl font-bold text-white mb-1">
            Find your next meal
          </h1>
          <p className="text-sm text-white/60 max-w-md">
            Browse restaurants, cafes, and bars. Order meals and pay from your simulation wallet.
          </p>
        </div>
      </div>

      {/* City grid */}
      {browseVenues.length === 0 && !browseLoading && !browseError && (
        <div className="mb-6">
          <h2 className="ls-heading text-sm font-bold mb-3">Popular locations</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {(presetCities.length > 0
              ? presetCities.slice(0, 6).map((c) => {
                  const preset = PRESET_LOCATIONS.find((p) => p.name === c) || PRESET_LOCATIONS[0];
                  return { ...preset, name: c };
                })
              : PRESET_LOCATIONS
            ).map((loc) => (
              <button
                key={loc.name}
                className="group relative overflow-hidden rounded-lg cursor-pointer"
                style={{ aspectRatio: '3/2' }}
                onClick={() => { handleCityClick(loc.name); jumpTo(loc); }}
                data-testid={`ls-food-city-${loc.name}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${loc.image})` }}
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.15) 100%)' }} />
                <div className="absolute bottom-2 left-2 right-2 z-10">
                  <p className="text-white font-bold text-sm">{loc.name}</p>
                  <p className="text-white/60 text-[10px]">{loc.country}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="relative mb-4" ref={suggestionsRef}>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ls-muted)' }} />
            <input
              type="text" className="ls-input w-full !pl-9 !py-2.5 text-sm"
              placeholder="Search any city worldwide..."
              value={locationQuery}
              onChange={(e) => setLocationQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLocationSearch()}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              data-testid="ls-food-location-search"
            />
            {geocodeLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--ls-primary)', borderTopColor: 'transparent' }} />
            )}
          </div>
          <button className="ls-btn-primary !py-2.5 text-sm" onClick={handleLocationSearch}>
            <MagnifyingGlass size={14} weight="bold" /> Search
          </button>
        </div>
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 z-50 ls-surface shadow-lg rounded-lg overflow-hidden" style={{ border: '1px solid var(--ls-border)' }}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors hover:bg-[var(--ls-elevated)]"
                onClick={() => handleLocationSelect(s)}
              >
                <MapPinLine size={14} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                <span style={{ color: 'var(--ls-secondary)' }}>{s.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      {(browseVenues.length > 0 || browseLoading || selectedCities.length > 0) && (
        <FoodFilters
          cities={presetCities.length > 0 ? presetCities : PRESET_LOCATIONS.map((l) => l.name)}
          selectedCities={selectedCities}
          onCitiesChange={(c) => { setSelectedCities(c); setActiveLocation(null); }}
          priceMin={priceMin} priceMax={priceMax}
          onPriceChange={(side, val) => side === 'min' ? setPriceMin(val) : setPriceMax(val)}
          subcategory={subcategoryFilter}
          onSubcategoryChange={(sc) => { setSubcategoryFilter(sc); setBrowseOffset(0); setPendingBrowse(true); }}
          searchText={searchText}
          onSearchChange={setSearchText}
          onApply={() => { setBrowseOffset(0); doBrowse(false); }}
          onClear={() => {
            setSearchText(''); setSubcategoryFilter(null);
            setPriceMin(null); setPriceMax(null);
            setBrowseOffset(0);
            if (selectedCities.length > 0 || activeLocation) doBrowse(false);
          }}
          loading={browseLoading}
        />
      )}

      {/* Results toolbar */}
      {(browseVenues.length > 0 || browseLoading) && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs" style={{ color: 'var(--ls-muted)' }}>
            {browseTotal > 0 && `${browseTotal} venue${browseTotal !== 1 ? 's' : ''} found`}
            {activeLocation && <span> near <strong>{activeLocation}</strong></span>}
            {!activeLocation && selectedCities.length > 0 && <span> in <strong>{selectedCities.join(', ')}</strong></span>}
          </p>
          <div className="flex items-center gap-1">
            <button
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? '' : ''}`}
              style={{
                background: viewMode === 'cards' ? 'var(--ls-primary)' : 'var(--ls-elevated)',
                color: viewMode === 'cards' ? '#fff' : 'var(--ls-muted)',
              }}
              onClick={() => setViewMode('cards')}
              title="Card view"
            >
              <ListBullets size={14} weight="bold" />
            </button>
            <button
              className="p-1.5 rounded-md transition-colors"
              style={{
                background: viewMode === 'map' ? 'var(--ls-primary)' : 'var(--ls-elevated)',
                color: viewMode === 'map' ? '#fff' : 'var(--ls-muted)',
              }}
              onClick={() => setViewMode('map')}
              title="Map view"
            >
              <MapTrifold size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {browseError && (
        <div className="ls-elevated p-4 rounded-lg mb-4 text-center">
          <p className="text-sm" style={{ color: 'var(--ls-danger)' }}>{browseError}</p>
          <button className="ls-btn-ghost text-xs mt-2" onClick={() => doBrowse(false)}>Try again</button>
        </div>
      )}

      {/* Content: cards or map */}
      {viewMode === 'cards' ? (
        <>
          {browseVenues.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" style={{ gridAutoRows: '280px' }}>
              {browseVenues.map((v) => (
                <VenueCard
                  key={v.venue_id}
                  venue={v}
                  currencySymbol={currencySymbol}
                  onViewDetails={handleViewDetails}
                  onBook={handleQuickBook}
                  bookDisabledReason={bookDisabledReason}
                />
              ))}
            </div>
          )}
          {browseVenues.length > 0 && browseVenues.length < browseTotal && (
            <div className="text-center mt-6">
              <button
                className="ls-btn-ghost text-sm"
                onClick={() => doBrowse(true)}
                disabled={browseLoading}
              >
                {browseLoading ? 'Loading…' : (
                  <>Load more <ArrowRight size={12} weight="bold" /></>
                )}
              </button>
            </div>
          )}
          {!browseLoading && browseVenues.length === 0 && !browseError && initialLoadDone && (
            <div className="text-center py-16">
              <ForkKnife size={48} weight="duotone" style={{ color: 'var(--ls-muted)', margin: '0 auto' }} />
              <h3 className="ls-heading text-lg font-bold mt-4">No restaurants found</h3>
              <p className="text-sm mt-1" style={{ color: 'var(--ls-muted)' }}>Try a different city or adjust your filters</p>
            </div>
          )}
          {browseLoading && browseVenues.length === 0 && (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: 'var(--ls-primary)', borderTopColor: 'transparent' }} />
              <p className="text-sm mt-4" style={{ color: 'var(--ls-muted)' }}>Searching restaurants…</p>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ height: 'max(450px, 60vh)', border: '1px solid var(--ls-border)' }}>
          <VenueMap
            key={mapKey} exerciseId={exerciseId} category="restaurant"
            center={center} zoom={zoom}
            onSelectVenue={setSelectedVenue}
          />
          {selectedVenue && (
            <VenueDetailCard
              venue={selectedVenue} currencySymbol={currencySymbol}
              onBook={() => handleViewDetails(selectedVenue)}
              onClose={() => setSelectedVenue(null)}
              bookDisabledReason={bookDisabledReason}
            />
          )}
        </div>
      )}

      {/* Restaurant detail panel */}
      <RestaurantDetailPanel
        open={!!detailPanelVenue}
        onClose={() => setDetailPanelVenue(null)}
        exerciseId={exerciseId}
        venue={detailPanelVenue}
        currencySymbol={currencySymbol}
        onOrder={handleOrder}
        bookDisabledReason={bookDisabledReason}
      />

      {/* Food order drawer */}
      <FoodOrderDrawer
        open={!!orderDrawerVenue}
        onClose={() => { setOrderDrawerVenue(null); setOrderDrawerItems([]); }}
        exerciseId={exerciseId}
        venue={orderDrawerVenue}
        items={orderDrawerItems}
        wallets={wallets}
        currencySymbol={currencySymbol}
        onOrdered={(order, wallet) => {
          upsertWallet(wallet);
          loadWallets();
        }}
      />
    </div>
  );
};

const LogisticsFoodPage = () => (
  <LogisticsLayout>
    <FoodContent />
  </LogisticsLayout>
);

export default LogisticsFoodPage;
